import type { UserRole } from '@rundown-studio/types'
import type { FC, PropsWithChildren } from 'react'
import { useDashboardPermission } from '../lib/use-dashboard-permission'

/**
 * A guard component that conditionally renders children based on user permissions.
 *
 * This component uses a bitmask permission system to control access to UI elements.
 * It supports both hierarchical role checks and specific permission flag checks.
 *
 * @param props - Component props
 * @param props.role - Check if user has at least this role level (hierarchical check)
 * @param props.permissions - Check if user has specific permission flags (bitmask check)
 * @param props.children - Content to render if user has required permissions
 *
 * @returns The children if user has permissions, `null` otherwise
 *
 * @example
 * // Show content only to TEAM_EDITOR and above (hierarchical)
 * <UserPermissionGuard role={UserRole.TEAM_EDITOR}>
 *   <TeamSettingsPanel />
 * </UserPermissionGuard>
 *
 * @example
 * // Show content only to users with specific team edit permission
 * <DashboardPermissionGuard permissions={DASHBOARD_PERMISSIONS.TEAM_EDIT}>
 *   <EditTeamButton />
 * </DashboardPermissionGuard>
 *
 * @example
 * // Show content to users with multiple permissions (team view AND event edit)
 * <DashboardPermissionGuard
 *   permissions={DASHBOARD_PERMISSIONS.TEAM_VIEW | DASHBOARD_PERMISSIONS.EVENT_EDIT}
 * >
 *   <AdvancedControls />
 * </DashboardPermissionGuard>
 *
 * @example
 * // Conditional rendering with fallback
 * <DashboardPermissionGuard role={UserRole.TEAM_ADMIN}>
 *   <AdminPanel />
 * </DashboardPermissionGuard>
 * {/* Content below will show for everyone, including non-admins *\/}
 * <RegularUserContent />
 *
 * @note
 * - Only pass either `role` OR `permissions`, not both
 * - `role` performs hierarchical check (TEAM_ADMIN can access TEAM_EDITOR content)
 * - `permissions` performs bitmask check against specific permission flags
 * - Component returns `null` if user lacks permissions (no DOM node rendered)
 * - Consider showing a fallback message for better UX when access is denied
 *
 * @see {@link useDashboardPermission} for the underlying permission logic
 * @see {@link DashboardPermissions} for available permission flags
 * @see {@link UserRole} for available roles
 */
export const DashboardPermissionGuard: FC<
  PropsWithChildren<{
    permissions?: number
    role?: UserRole
  }>
> = ({ role, permissions, children }) => {
  const hasPermission = useDashboardPermission({
    role,
    permissions,
  })

  if (hasPermission) return children

  return null
}
